import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ServerService } from 'shared/core/services/server.service';
import {
    SupportScreenshot,
    SupportScreenshotService,
} from 'shared/core/services/support-screenshot.service';
import { UserService } from 'shared/core/services/user/user.service';

@Component({
    selector: 'app-support-dialog',
    imports: [FormsModule],
    templateUrl: './support-dialog.component.html',
    styleUrl: './support-dialog.component.scss',
})
export class SupportDialogComponent implements OnInit {
    private changeDetector = inject(ChangeDetectorRef);
    public context: Record<string, unknown> = {};
    public subject = '';
    public description = '';
    public requester = '';
    public checking = true;
    public enabled = false;
    public sending = false;
    public reading = false;
    public capturing = false;
    public error = '';
    public fileName = '';
    public attachment: { base64: string; mime: string } | undefined;
    public pageScreenshot: SupportScreenshot | undefined;
    public result: {
        ticket_id: number;
        context_saved: boolean;
        ticket_url?: string;
        routing_verified?: boolean | null;
    } | null = null;
    public payload: Record<string, unknown> | null = null;

    constructor(
        public activeModal: NgbActiveModal,
        private server: ServerService,
        private user: UserService,
        private screenshot: SupportScreenshotService
    ) {}

    public ngOnInit(): void {
        this.requester = this.user.user?.email || '';
        this.checkConfiguration();
    }

    public get screenshotAvailable(): boolean {
        return this.screenshot.available;
    }

    public async captureScreenshot(): Promise<void> {
        if (this.capturing || this.sending || this.reading || this.payload) return;
        this.capturing = true;
        this.error = '';
        try {
            this.pageScreenshot = await this.screenshot.capture();
            this.context.page_screenshot_status = 'captured';
            this.context.page_screenshot_captured_at = new Date().toISOString();
        } catch (error) {
            if (error.name !== 'NotAllowedError') {
                this.error =
                    error.message ||
                    'Unable to capture the screen. Attach a screenshot below.';
                if (!this.pageScreenshot)
                    this.context.page_screenshot_status = 'unavailable';
            }
        } finally {
            this.capturing = false;
            this.changeDetector.markForCheck();
        }
    }

    public removeCapture(): void {
        if (this.capturing || this.sending || this.payload) return;
        this.pageScreenshot = undefined;
        this.context.page_screenshot_status = 'not_requested';
        delete this.context.page_screenshot_captured_at;
    }

    public checkConfiguration(): void {
        this.checking = true;
        this.error = '';
        this.server.get(
            'support/configuration',
            {},
            data => {
                this.enabled = data?.enabled === true;
                this.checking = false;
                if (!this.enabled)
                    this.error =
                        'Support is not configured for your organisation. Contact your Pulse administrator.';
            },
            () => {
                this.checking = false;
                this.error =
                    'Unable to connect to support. Check your connection and try again.';
            },
            undefined,
            true
        );
    }

    public async chooseScreenshot(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        this.attachment = undefined;
        this.fileName = '';
        this.error = '';
        if (!file) return;
        if (
            !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
            file.size > 5 * 1024 * 1024
        ) {
            this.error = 'Choose a PNG, JPEG or WebP screenshot up to 5 MB.';
            input.value = '';
            return;
        }
        this.reading = true;
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (): void => resolve(String(reader.result).split(',')[1]);
                reader.onerror = (): void =>
                    reject(
                        new Error('Unable to read the screenshot. Choose the file again.')
                    );
                reader.readAsDataURL(file);
            });
            this.attachment = { base64, mime: file.type };
            this.fileName = file.name;
        } catch (error) {
            this.error = error.message;
        } finally {
            this.reading = false;
            this.changeDetector.markForCheck();
        }
    }

    public submit(): void {
        if (
            this.sending ||
            this.reading ||
            this.capturing ||
            !this.enabled ||
            this.result?.context_saved
        )
            return;
        if (!this.payload) {
            if (!this.subject.trim() || !this.description.trim()) return;
            this.payload = {
                subject: this.subject.trim(),
                description: this.description.trim(),
                request_id: crypto.randomUUID(),
                context: this.context,
                ...(this.attachment ? { attachment: this.attachment } : {}),
                ...(this.pageScreenshot ? { page_screenshot: this.pageScreenshot } : {}),
            };
        }
        this.sending = true;
        this.error = '';
        this.server.post(
            'support/createTicket',
            this.payload,
            data => {
                this.result = data;
                this.sending = false;
            },
            error => {
                this.error =
                    error?.error?.msg ||
                    'Unable to send your request. Check your connection and try again.';
                this.sending = false;
            },
            undefined,
            true
        );
    }
}
